/** Расчёт геометрии QR на целочисленной сетке модулей. */

export type QrFit = {
  /** Сторона одного модуля в пикселях, всегда целое число. */
  moduleSize: number;
  /** Сторона всей области QR вместе с защитным полем. */
  sizePx: number;
  /** Остаток доступного места, который уходит во внешний отступ. */
  slackPx: number;
  matrixSize: number;
  quietZoneModules: number;
};

/**
 * Минимальный размер модуля, при котором мы ещё готовы отдать файл.
 * Это ограничение продукта, а не универсальная гарантия сканирования.
 */
export const MIN_MODULE_PX = 2;

/** Ниже этого значения печатать бессмысленно, показываем предупреждение. */
export const COMFORTABLE_MODULE_PX = 4;

/**
 * Вписывает матрицу в доступную ширину.
 *
 *   moduleSizePx  = floor(W / (N + 2q))
 *   actualQrSizePx = moduleSizePx * (N + 2q)
 *
 * Остаток не растягивает модули, а уходит наружу как дополнительный отступ.
 */
export function fitQr(availableWidthPx: number, matrixSize: number, quietZoneModules: number): QrFit {
  const total = matrixSize + 2 * quietZoneModules;
  const moduleSize = Math.floor(availableWidthPx / total);
  const sizePx = moduleSize * total;
  return {
    moduleSize,
    sizePx,
    slackPx: availableWidthPx - sizePx,
    matrixSize,
    quietZoneModules,
  };
}

export type Rect = { x: number; y: number; w: number; h: number };

export function rectsOverlap(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** Относительная яркость по формуле WCAG — используется только как эвристика. */
export function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  const channel = (v: number): number => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const light = Math.max(la, lb);
  const dark = Math.min(la, lb);
  return (light + 0.05) / (dark + 0.05);
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  let value = m[1]!;
  if (value.length === 3) {
    value = value
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = Number.parseInt(value, 16);
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

/** Строгая проверка цвета: принимаем только #rgb и #rrggbb, никакого CSS. */
export function isSafeHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value.trim());
}
